import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Colors } from "../../utils/Colors";

const ADMISSION_API =
  "http://127.0.0.1:8000/api/patients/admissions/?format=json";
const MEDICATION_API =
  "http://127.0.0.1:8000/api/medcharts/medication-chart/?format=json";
const MEDICATION_CHOICES_API =
  "http://127.0.0.1:8000/api/medcharts/medication-chart/choices/?format=json";

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: `1px solid ${Colors.border.input}`,
  outline: "none",
  fontSize: "14px",
  background: Colors.bg.paper,
  boxSizing: "border-box",
  color: Colors.text.body,
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "110px",
  resize: "vertical",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: 700,
  color: Colors.text.title,
  fontSize: "14px",
};

const readOnlyStyle = {
  ...inputStyle,
  background: Colors.bg.hover,
  color: Colors.text.muted,
};

const sectionCardStyle = {
  background: Colors.bg.paper,
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 4px 14px rgba(11,60,63,0.05)",
};

export default function MedicationChartForm() {
  const [admissions, setAdmissions] = useState([]);
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);

  const [typeOptions, setTypeOptions] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [loadingChoices, setLoadingChoices] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [form, setForm] = useState({
    admission: "",
    admission_number: "",
    patient_uhid: "",
    patient_name: "",
    doctor_name: "",
    type: "",
    generic: "",
    brand: "",
    route: "",
    dose: "",
    frequency: "",
    duration: "",
    side: "",
  });

  useEffect(() => {
    fetchAdmissions();
    fetchChoices();
  }, []);

  const fetchAdmissions = async () => {
    try {
      setLoadingAdmissions(true);
      setErrorMsg("");

      const res = await axios.get(ADMISSION_API);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setAdmissions(data);
    } catch (error) {
      console.error("Admissions fetch error:", error);
      setErrorMsg("Unable to load admissions.");
    } finally {
      setLoadingAdmissions(false);
    }
  };

  const fetchChoices = async () => {
    try {
      setLoadingChoices(true);

      const res = await axios.get(MEDICATION_CHOICES_API);
      const data = res.data || {};

      setTypeOptions(Array.isArray(data.type_choices) ? data.type_choices : []);
      setRouteOptions(Array.isArray(data.route_choices) ? data.route_choices : []);
      setFrequencyOptions(
        Array.isArray(data.frequency_choices) ? data.frequency_choices : []
      );
    } catch (error) {
      console.error("Medication choices fetch error:", error);

      setTypeOptions([
        { value: "ANTIBIOTIC", label: "Antibiotic" },
        { value: "ANALGESIC", label: "Analgesic" },
        { value: "ANTIPYRETIC", label: "Antipyretic" },
        { value: "ANTACID", label: "Antacid" },
        { value: "ANTIHYPERTENSIVE", label: "Antihypertensive" },
        { value: "ANTIDIABETIC", label: "Antidiabetic" },
        { value: "INSULIN", label: "Insulin" },
        { value: "IV_FLUID", label: "IV Fluid" },
        { value: "VITAMIN", label: "Vitamin" },
        { value: "OTHER", label: "Other" },
      ]);

      setRouteOptions([
        { value: "ORAL", label: "Oral" },
        { value: "IV", label: "IV" },
        { value: "IM", label: "IM" },
        { value: "SC", label: "SC" },
        { value: "TOPICAL", label: "Topical" },
        { value: "INHALATION", label: "Inhalation" },
        { value: "RECTAL", label: "Rectal" },
        { value: "NASAL", label: "Nasal" },
        { value: "OTHER", label: "Other" },
      ]);

      setFrequencyOptions([
        { value: "OD", label: "OD - Once Daily" },
        { value: "BD", label: "BD - Twice Daily" },
        { value: "TDS", label: "TDS - Three Times Daily" },
        { value: "QID", label: "QID - Four Times Daily" },
        { value: "SOS", label: "SOS - As Needed" },
        { value: "STAT", label: "STAT - Immediately" },
        { value: "Q4H", label: "Every 4 Hours" },
        { value: "Q6H", label: "Every 6 Hours" },
        { value: "Q8H", label: "Every 8 Hours" },
        { value: "Q12H", label: "Every 12 Hours" },
      ]);
    } finally {
      setLoadingChoices(false);
    }
  };

  const selectedAdmission = useMemo(() => {
    return admissions.find(
      (item) =>
        String(item.admission_id || item.id) === String(form.admission)
    );
  }, [admissions, form.admission]);

  useEffect(() => {
    if (!selectedAdmission) {
      setForm((prev) => ({
        ...prev,
        admission_number: "",
        patient_uhid: "",
        patient_name: "",
        doctor_name: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      admission_number:
        selectedAdmission.admission_number ||
        selectedAdmission.admission_no ||
        selectedAdmission.ip_number ||
        selectedAdmission.ip_no ||
        "",

      patient_uhid:
        selectedAdmission.patient_uhid ||
        selectedAdmission.patient_uhid_out ||
        selectedAdmission.uhid ||
        "",

      patient_name:
        selectedAdmission.patient_name ||
        selectedAdmission.name ||
        selectedAdmission.full_name ||
        "",

      doctor_name:
        selectedAdmission.treating_doctor ||
        selectedAdmission.attending_doctor ||
        selectedAdmission.consultant ||
        selectedAdmission.doctor_name ||
        "",
    }));
  }, [selectedAdmission]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMsg("");
    setSuccessMsg("");

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm({
      admission: "",
      admission_number: "",
      patient_uhid: "",
      patient_name: "",
      doctor_name: "",
      type: "",
      generic: "",
      brand: "",
      route: "",
      dose: "",
      frequency: "",
      duration: "",
      side: "",
    });
  };

  const validateForm = () => {
    if (!form.admission) return "Please select admission.";
    if (!form.type) return "Please select medication type.";
    if (!form.generic.trim()) return "Please enter generic name.";
    if (!form.brand.trim()) return "Please enter brand name.";
    if (!form.route) return "Please select route.";
    if (!form.dose.trim()) return "Please enter dose.";
    if (!form.frequency) return "Please select frequency.";
    if (!form.duration.trim()) return "Please enter duration.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");

      const payload = {
        admission: form.admission,
        type: form.type,
        generic: form.generic.trim(),
        brand: form.brand.trim(),
        route: form.route,
        dose: form.dose.trim(),
        frequency: form.frequency,
        duration: form.duration.trim(),
        side: form.side.trim(),
      };

      const response = await axios.post(MEDICATION_API, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Medication saved:", response.data);
      setSuccessMsg("Medication chart saved successfully.");
      resetForm();
    } catch (error) {
      console.error("Medication save error:", error);

      if (error.response?.data) {
        setErrorMsg(
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
        );
      } else {
        setErrorMsg("Failed to save medication chart.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: Colors.bg.app,
        padding: "32px 18px",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          background: Colors.bg.paper,
          borderRadius: "24px",
          boxShadow: Colors.shadow.brand,
          overflow: "hidden",
          border: `1px solid ${Colors.border.light}`,
        }}
      >
        <div
          style={{
            padding: "26px 30px 18px",
            background: Colors.bg.paper,
            borderBottom: `1px solid ${Colors.border.light}`,
            position: "relative",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 800,
              color: Colors.text.title,
              letterSpacing: "0.2px",
            }}
          >
            Add Medication Chart
          </h2>

          <p
            style={{
              margin: "8px 0 0",
              color: Colors.text.muted,
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            Select admission and enter medicine details for the treatment plan.
          </p>

          <div
            style={{
              marginTop: "16px",
              height: "6px",
              width: "140px",
              borderRadius: "999px",
              background: Colors.brand.accentBar,
            }}
          />
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#f0fff4",
                border: `1px solid ${Colors.status.success}`,
                color: Colors.status.success,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {successMsg}
            </div>
          )}

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Admission Selection
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                Choose an admission to auto-fill patient information.
              </p>
            </div>

            <div>
              <label style={labelStyle}>Admission</label>
              <select
                name="admission"
                value={form.admission}
                onChange={handleChange}
                style={inputStyle}
                disabled={loadingAdmissions}
              >
                <option value="">
                  {loadingAdmissions ? "Loading admissions..." : "Select admission"}
                </option>

                {admissions.map((item) => {
                  const admissionId = item.admission_id || item.id;
                  const admissionNumber =
                    item.admission_number ||
                    item.admission_no ||
                    item.ip_number ||
                    item.ip_no ||
                    "";
                  const patientName =
                    item.patient_name || item.name || item.full_name || "";

                  return (
                    <option key={admissionId} value={admissionId}>
                      {admissionNumber} - {patientName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Patient Details
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                These details are auto-filled from the selected admission.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Admission Number</label>
                <input
                  type="text"
                  value={form.admission_number}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Patient UHID</label>
                <input
                  type="text"
                  value={form.patient_uhid}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Patient Name</label>
                <input
                  type="text"
                  value={form.patient_name}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Doctor Name</label>
                <input
                  type="text"
                  value={form.doctor_name}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Medication Details
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                Select dropdown values and fill the remaining medicine information manually.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Type</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={loadingChoices}
                >
                  <option value="">
                    {loadingChoices ? "Loading types..." : "Select type"}
                  </option>
                  {typeOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Generic</label>
                <input
                  type="text"
                  name="generic"
                  value={form.generic}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: Amoxicillin"
                />
              </div>

              <div>
                <label style={labelStyle}>Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={form.brand}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: Augmentin"
                />
              </div>

              <div>
                <label style={labelStyle}>Route</label>
                <select
                  name="route"
                  value={form.route}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={loadingChoices}
                >
                  <option value="">
                    {loadingChoices ? "Loading routes..." : "Select route"}
                  </option>
                  {routeOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Dose</label>
                <input
                  type="text"
                  name="dose"
                  value={form.dose}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: 500 mg"
                />
              </div>

              <div>
                <label style={labelStyle}>Frequency</label>
                <select
                  name="frequency"
                  value={form.frequency}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={loadingChoices}
                >
                  <option value="">
                    {loadingChoices ? "Loading frequency..." : "Select frequency"}
                  </option>
                  {frequencyOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: 7 days"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Side Effects</label>
                <textarea
                  name="side"
                  value={form.side}
                  onChange={handleChange}
                  style={textareaStyle}
                  placeholder="Example: Nausea, diarrhea"
                />
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: "28px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => {
                resetForm();
                setErrorMsg("");
                setSuccessMsg("");
              }}
              style={{
                padding: "12px 18px",
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                cursor: "pointer",
                fontWeight: 700,
                transition: "all 0.2s ease",
              }}
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "12px 22px",
                borderRadius: "12px",
                border: "none",
                background: saving
                  ? Colors.text.sub
                  : Colors.brand.primary,
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 800,
                minWidth: "190px",
                boxShadow: saving ? "none" : Colors.shadow.brand,
              }}
            >
              {saving ? "Saving..." : "Save Medication Chart"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}